// ResizeObserver hook: picks full/abbrev/dot project pill tier for scheduled blocks.
import { useLayoutEffect, useState } from 'react';
import { TASK_ROW_TITLE_MIN_WIDTH_PX } from '@/components/ui/buildTaskRowTitleTrailingSx';
import {
  computeScheduledTaskTitleRowPillBudgetPx,
  resolveProjectPillTier,
  SCHEDULED_TASK_TITLE_ROW_GAP_PX,
} from '@/features/tasks/utils/ui/projectPillDisplayTier';

/**
 * ResizeObserver tier selection for the project pill in a scheduled calendar block.
 * @param {import('react').RefObject<HTMLElement|null>} titleRowRef
 * @param {import('react').RefObject<HTMLElement|null>} measureRef
 * @param {boolean} hasProjectPill
 * @param {string} measureKey
 */
export function useScheduledTaskProjectPillTier(
  titleRowRef,
  measureRef,
  hasProjectPill,
  measureKey,
) {
  const [tier, setTier] = useState('full');

  useLayoutEffect(() => {
    if (!hasProjectPill) {
      setTier('full');
      return undefined;
    }

    const row = titleRowRef.current;
    const measure = measureRef.current;
    if (!row || !measure) return undefined;

    const readWidths = () => {
      const fullEl = measure.querySelector('[data-project-pill-measure="full"]');
      const abbrevEl = measure.querySelector(
        '[data-project-pill-measure="abbrev"]',
      );
      const dotEl = measure.querySelector('[data-project-pill-measure="dot"]');
      return {
        full: fullEl?.getBoundingClientRect().width ?? 0,
        abbrev: abbrevEl?.getBoundingClientRect().width ?? 0,
        dot: dotEl?.getBoundingClientRect().width ?? 0,
      };
    };

    const measurePillBudgetPx = () => {
      const checkbox = row.querySelector('[type="checkbox"]')?.closest('.MuiCheckbox-root');
      const checkboxWidthPx = checkbox?.getBoundingClientRect().width ?? 0;
      return computeScheduledTaskTitleRowPillBudgetPx({
        rowWidthPx: row.getBoundingClientRect().width,
        checkboxWidthPx,
        titleMinWidthPx: TASK_ROW_TITLE_MIN_WIDTH_PX,
        rowGapPx: SCHEDULED_TASK_TITLE_ROW_GAP_PX,
      });
    };

    const update = () => {
      const projectWidthsPx = readWidths();
      const next = resolveProjectPillTier({
        pillsColWidthPx: measurePillBudgetPx(),
        contextPillsWidthPx: 0,
        gapPx: 0,
        projectWidthsPx,
      });
      setTier((prev) => (prev === next ? prev : next));
    };

    update();
    if (typeof ResizeObserver === 'undefined') return undefined;

    const ro = new ResizeObserver(update);
    ro.observe(row);
    const titleEl = row.querySelector('[data-testid="scheduled-task-title"]');
    if (titleEl) ro.observe(titleEl);

    return () => ro.disconnect();
  }, [titleRowRef, measureRef, hasProjectPill, measureKey]);

  return tier;
}
